import { useQueryClient } from '@tanstack/react-query';

import { useSecretMutation } from '@/hooks/use-secret-mutation';

import { secretService } from '@/services/secret-service';
import type { JSONObject } from '@/types/shared/json-schema';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { secretMutationKeys, secretQueryKeys } from './query-keys';

/**
 * Hook to create a secret.
 * Returns a react-query mutation that accepts secret creation payload.
 * Invalidates the secrets cache to trigger a refetch.
 */
export function useCreateSecret(tenantId?: UUID) {
  const qc = useQueryClient();

  return useSecretMutation({
    mutationKey: secretMutationKeys.createSecret(tenantId),
    mutationFn: async (payload: {
      name: string;
      raw: JSONObject;
      templateId: string;
    }) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const secret = await secretService.createSecret(tenantId, payload);

      const projected = await pollForResource(() =>
        secretService.findSecret(tenantId, secret.secretId)
      );

      return projected ?? secret;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: secretQueryKeys.lists(),
        refetchType: 'all',
      });
    },
  });
}
